import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Brain,
  Scale,
  Leaf,
  ShieldCheck,
  Users,
  CheckCircle2,
  Lock,
  Info,
  Loader2,
  MapPin,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import {
  PROFESSIONAL_CATEGORIES,
  ProfessionalCategoryId,
} from '../types/professionalRates';

type VitaliteitsNiveau = 'A' | 'B' | 'C';
type Belastingcategorie = 'zwaar' | 'medium' | 'licht';

interface DemandRule {
  basis: number;
  achterwacht: number;
  totaal: number;
}

interface SchilInfo {
  schil: number;
  label: string;
  min: number;
  max: number | null;
}

interface DisciplineRow {
  professional_type: string;
  label: string;
  category: ProfessionalCategoryId | string;
  belastingcategorie: Belastingcategorie;
  is_medewerker_gericht: boolean;
  billing_route: 'agenda_uur' | 'milestone';
  min_pool_experts: number;
}

interface SelectieRow {
  professional_type: string;
  bron: 'niveau' | 'arbo' | 'handmatig';
  gewenste_experts: number;
}

interface PoolSetupPayload {
  config: {
    vitaliteits_niveau: VitaliteitsNiveau | null;
    arbo_module_actief: boolean;
    aantal_medewerkers: number | null;
    afgerond_op: string | null;
  } | null;
  selectie: SelectieRow[];
  disciplines: DisciplineRow[];
  niveaus: Record<VitaliteitsNiveau, string[]>;
  arbo_module: string[];
  aantal_medewerkers_profiel: number | null;
  demand_matrix: Record<Belastingcategorie, Record<number, DemandRule>>;
  schillen: SchilInfo[];
  schil_actief: number;
}

/** Gedifferentieerde staffel: schil naar bedrijfsgrootte (spiegel van server) */
function schilVoor(aantal: number): number {
  if (aantal <= 250) return 1;
  if (aantal <= 500) return 2;
  if (aantal <= 1000) return 3;
  return 4;
}

const BELASTING_META: Record<Belastingcategorie, { dot: string; badge: string }> = {
  zwaar: { dot: 'bg-red-500', badge: 'bg-red-50 text-red-700 border-red-200' },
  medium: { dot: 'bg-amber-400', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  licht: { dot: 'bg-emerald-500', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

const NIVEAU_ICONS: Record<VitaliteitsNiveau, React.ComponentType<{ className?: string }>> = {
  A: Brain,
  B: Scale,
  C: Leaf,
};

const CATEGORY_ORDER: string[] = ['psa', 'trauma', 'welzijn', 'ld', 'arbo'];

export const EmployerPoolSetupPage: React.FC = () => {
  const { t } = useTranslation('employer');
  const navigate = useNavigate();

  const [payload, setPayload] = useState<PoolSetupPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Wizard-state
  const [aantal, setAantal] = useState<string>('');
  const [niveau, setNiveau] = useState<VitaliteitsNiveau | null>(null);
  const [arboActief, setArboActief] = useState(false);
  const [extra, setExtra] = useState<Set<string>>(new Set());

  useEffect(() => {
    (async () => {
      try {
        const response = await api.get('/employer/pool-setup');
        const data: PoolSetupPayload = response.data.data;
        setPayload(data);
        const bestaandAantal = data.config?.aantal_medewerkers ?? data.aantal_medewerkers_profiel;
        if (bestaandAantal) setAantal(String(bestaandAantal));
        setNiveau(data.config?.vitaliteits_niveau ?? null);
        setArboActief(data.config?.arbo_module_actief ?? false);
        setExtra(new Set(data.selectie.filter((s) => s.bron === 'handmatig').map((s) => s.professional_type)));
      } catch {
        toast.error(t('poolSetup.loadError'));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const aantalNum = parseInt(aantal, 10) || 0;
  const schil = schilVoor(aantalNum);
  const schilInfo = payload?.schillen?.find((s) => s.schil === schil) ?? null;

  const arboSet = useMemo(() => new Set(payload?.arbo_module ?? []), [payload]);

  const categorieVoor = useMemo(() => {
    const map: Record<string, Belastingcategorie> = {};
    for (const d of payload?.disciplines ?? []) map[d.professional_type] = d.belastingcategorie;
    return map;
  }, [payload]);

  /** Vereiste dekkingsregel (basis + achterwacht) per discipline in deze schil */
  const vereist = useMemo(() => {
    return (type: string): DemandRule => {
      const cat = categorieVoor[type] ?? 'licht';
      return payload?.demand_matrix?.[cat]?.[schil] ?? { basis: 1, achterwacht: 1, totaal: 2 };
    };
  }, [categorieVoor, payload, schil]);
  const niveauSet = useMemo(
    () => new Set(niveau && payload ? payload.niveaus[niveau] : []),
    [niveau, payload]
  );

  const labelVoor = useMemo(() => {
    const map: Record<string, string> = {};
    for (const d of payload?.disciplines ?? []) map[d.professional_type] = d.label;
    return map;
  }, [payload]);

  /** Definitieve selectie met bron, in dezelfde prioriteit als de server */
  const selectie = useMemo(() => {
    const map = new Map<string, 'niveau' | 'arbo' | 'handmatig'>();
    niveauSet.forEach((type) => map.set(type, 'niveau'));
    if (arboActief) {
      for (const type of arboSet) if (!map.has(type)) map.set(type, 'arbo');
    }
    for (const type of extra) if (!map.has(type) && !arboSet.has(type)) map.set(type, 'handmatig');
    return map;
  }, [niveauSet, arboActief, arboSet, extra]);

  const totaalExperts = useMemo(() => {
    let som = 0;
    selectie.forEach((_bron, type) => {
      som += vereist(type).totaal;
    });
    return som;
  }, [selectie, vereist]);

  const vitaliteitsDisciplines = Array.from(selectie.keys()).filter((type) => !arboSet.has(type));
  const arboDisciplines = Array.from(selectie.keys()).filter((type) => arboSet.has(type));
  const vitaliteitsExperts = vitaliteitsDisciplines.reduce((som, type) => som + vereist(type).totaal, 0);
  const arboExperts = arboDisciplines.reduce((som, type) => som + vereist(type).totaal, 0);

  const disciplinesPerCategorie = useMemo(() => {
    const groepen: Record<string, DisciplineRow[]> = {};
    for (const d of payload?.disciplines ?? []) {
      (groepen[d.category] ||= []).push(d);
    }
    return groepen;
  }, [payload]);

  const toggleExtra = (type: string) => {
    setExtra((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  const opslaan = async () => {
    if (!aantalNum || aantalNum < 1) {
      toast.error(t('poolSetup.aantalRequired'));
      return;
    }
    if (selectie.size === 0) {
      toast.error(t('poolSetup.selectionRequired'));
      return;
    }
    setSaving(true);
    try {
      const response = await api.put('/employer/pool-setup', {
        aantal_medewerkers: aantalNum,
        vitaliteits_niveau: niveau,
        arbo_module_actief: arboActief,
        extra_disciplines: Array.from(extra),
      });
      setPayload(response.data.data);
      toast.success(t('poolSetup.saved'));
    } catch {
      toast.error(t('poolSetup.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (!payload) {
    return (
      <div className="p-6 text-sm text-gray-600">{t('poolSetup.loadError')}</div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('poolSetup.title')}</h1>
        <p className="text-sm text-gray-600 mt-1">{t('poolSetup.subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Stap 1: bedrijfsgrootte */}
          <section className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="text-base font-semibold text-gray-900 flex items-center">
              <Users className="w-5 h-5 mr-2 text-blue-600" />
              {t('poolSetup.step1Title')}
            </h2>
            <p className="text-sm text-gray-600 mt-1 mb-4">{t('poolSetup.step1Help')}</p>
            <div className="flex flex-wrap items-center gap-3">
              <input
                type="number"
                min={1}
                value={aantal}
                onChange={(e) => setAantal(e.target.value)}
                placeholder="250"
                className="w-36 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <span className="text-sm text-gray-700">{t('poolSetup.medewerkers')}</span>
              {aantalNum > 0 && schilInfo && (
                <span className="text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-3 py-1">
                  {t('poolSetup.schilBadge', { schil, label: schilInfo.label })}
                </span>
              )}
            </div>
            <div className="mt-3 text-xs text-gray-500">
              {t('poolSetup.schilExplain')}
            </div>
          </section>

          {/* Stap 2: vitaliteitsniveau */}
          <section className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="text-base font-semibold text-gray-900">{t('poolSetup.step2Title')}</h2>
            <p className="text-sm text-gray-600 mt-1 mb-4">{t('poolSetup.step2Help')}</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {(['A', 'B', 'C'] as VitaliteitsNiveau[]).map((code) => {
                const Icon = NIVEAU_ICONS[code];
                const actief = niveau === code;
                const aantalDisciplines = payload.niveaus[code].length;
                const niveauExperts = payload.niveaus[code].reduce(
                  (som, type) => som + vereist(type).totaal,
                  0
                );
                return (
                  <button
                    key={code}
                    type="button"
                    onClick={() => setNiveau(actief ? null : code)}
                    className={`text-left rounded-xl border-2 p-4 transition-colors ${
                      actief
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 bg-white hover:border-blue-300'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <Icon className={`w-6 h-6 ${actief ? 'text-blue-600' : 'text-gray-400'}`} />
                      {actief && <CheckCircle2 className="w-5 h-5 text-blue-600" />}
                    </div>
                    <div className="font-semibold text-gray-900 text-sm">
                      {t(`poolSetup.niveau${code}Title`)}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      {t(`poolSetup.niveau${code}Subtitle`)}
                    </div>
                    <div className="text-xs text-gray-500 mt-2">
                      {t(`poolSetup.niveau${code}Kerntaak`)}
                    </div>
                    <div className="mt-3 text-xs font-medium text-blue-700">
                      {t('poolSetup.niveauCount', {
                        disciplines: aantalDisciplines,
                        experts: niveauExperts,
                      })}
                    </div>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Stap 3: Arbo-module (geïsoleerde add-on) */}
          <section className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-base font-semibold text-gray-900 flex items-center">
                  <ShieldCheck className="w-5 h-5 mr-2 text-emerald-600" />
                  {t('poolSetup.arboTitle')}
                </h2>
                <p className="text-sm text-gray-600 mt-1">{t('poolSetup.arboHelp')}</p>
                <p className="text-xs text-gray-500 mt-2 flex items-start">
                  <Info className="w-4 h-4 mr-1 flex-shrink-0 mt-0.5" />
                  {t('poolSetup.arboNote')}
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={arboActief}
                onClick={() => setArboActief((v) => !v)}
                className={`relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full transition-colors ${
                  arboActief ? 'bg-emerald-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                    arboActief ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            {arboActief && (
              <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2">
                {payload.arbo_module.map((type) => {
                  const regel = vereist(type);
                  return (
                    <li key={type} className="flex items-center text-sm text-gray-700">
                      <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-600 flex-shrink-0" />
                      {labelVoor[type] || type}
                      <span className="ml-2 text-xs text-gray-400">
                        {t('poolSetup.dekkingKort', {
                          totaal: regel.totaal,
                          basis: regel.basis,
                          achterwacht: regel.achterwacht,
                        })}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {/* Stap 4: interactieve menukaart */}
          <section className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="text-base font-semibold text-gray-900">{t('poolSetup.menuTitle')}</h2>
            <p className="text-sm text-gray-600 mt-1 mb-4">{t('poolSetup.menuHelp')}</p>
            <div className="space-y-5">
              {CATEGORY_ORDER.filter((cat) => disciplinesPerCategorie[cat]?.length).map((cat) => (
                <div key={cat}>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                    {PROFESSIONAL_CATEGORIES[cat as ProfessionalCategoryId]?.label || cat}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {disciplinesPerCategorie[cat].map((d) => {
                      const bron = selectie.get(d.professional_type);
                      // Arbo-disciplines zijn alleen via de Arbo-module te activeren
                      const vast = bron === 'niveau' || bron === 'arbo' || arboSet.has(d.professional_type);
                      const aangevinkt = selectie.has(d.professional_type);
                      const regel = vereist(d.professional_type);
                      const meta = BELASTING_META[d.belastingcategorie] ?? BELASTING_META.licht;
                      return (
                        <label
                          key={d.professional_type}
                          className={`flex items-center rounded-lg border px-3 py-2 text-sm ${
                            aangevinkt
                              ? 'border-blue-200 bg-blue-50 text-gray-900'
                              : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                          } ${vast ? 'cursor-default' : 'cursor-pointer'}`}
                        >
                          <input
                            type="checkbox"
                            checked={aangevinkt}
                            disabled={vast}
                            onChange={() => toggleExtra(d.professional_type)}
                            className="mr-3 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span
                            className={`mr-2 inline-block h-2 w-2 flex-shrink-0 rounded-full ${meta.dot}`}
                            title={t(`poolSetup.belasting_${d.belastingcategorie}`)}
                          />
                          <span className="flex-1">{d.label}</span>
                          {aangevinkt && (
                            <span
                              className={`ml-2 inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${meta.badge}`}
                            >
                              {t('poolSetup.dekkingKort', {
                                totaal: regel.totaal,
                                basis: regel.basis,
                                achterwacht: regel.achterwacht,
                              })}
                            </span>
                          )}
                          {aangevinkt && (
                            <button
                              type="button"
                              title={t('poolSetup.mapLink')}
                              aria-label={t('poolSetup.mapLink')}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                navigate(`/employer/dashboard/zoeken/kaart?professional_type=${encodeURIComponent(d.professional_type)}`);
                              }}
                              className="ml-2 inline-flex items-center justify-center rounded-md p-1 text-blue-600 hover:bg-blue-100"
                            >
                              <MapPin className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {vast && (
                            <span className="ml-2 inline-flex items-center text-xs text-blue-600">
                              <Lock className="w-3 h-3 mr-1" />
                              {bron === 'arbo' ? t('poolSetup.bronArbo') : t('poolSetup.bronNiveau')}
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Zijbalk: realtime rekenoverzicht */}
        <aside className="lg:col-span-1">
          <div className="lg:sticky lg:top-6 bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="text-base font-semibold text-gray-900 mb-4">
              {t('poolSetup.sidebarTitle')}
            </h2>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-600">{t('poolSetup.sidebarNiveau')}</dt>
                <dd className="font-medium text-gray-900">
                  {niveau ? t(`poolSetup.niveau${niveau}Title`) : '—'}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-600">{t('poolSetup.sidebarArbo')}</dt>
                <dd className="font-medium text-gray-900">
                  {arboActief ? t('poolSetup.aan') : t('poolSetup.uit')}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-600">{t('poolSetup.sidebarMedewerkers')}</dt>
                <dd className="font-medium text-gray-900">{aantalNum || '—'}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-600">{t('poolSetup.sidebarSchil')}</dt>
                <dd className="font-medium text-gray-900">
                  {schilInfo ? t('poolSetup.sidebarSchilWaarde', { schil, label: schilInfo.label }) : '—'}
                </dd>
              </div>
              <div className="border-t border-gray-100 pt-3 flex justify-between">
                <dt className="text-gray-600">{t('poolSetup.sidebarVitaliteit')}</dt>
                <dd className="font-medium text-gray-900">
                  {t('poolSetup.sidebarExpertsWaarde', {
                    disciplines: vitaliteitsDisciplines.length,
                    experts: vitaliteitsExperts,
                  })}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-600">{t('poolSetup.sidebarArboRegel')}</dt>
                <dd className="font-medium text-gray-900">
                  {t('poolSetup.sidebarExpertsWaarde', {
                    disciplines: arboDisciplines.length,
                    experts: arboExperts,
                  })}
                </dd>
              </div>
              <div className="border-t border-gray-200 pt-3 flex justify-between text-base">
                <dt className="font-semibold text-gray-900">{t('poolSetup.sidebarTotaal')}</dt>
                <dd className="font-bold text-blue-700">
                  {t('poolSetup.sidebarTotaalWaarde', { experts: totaalExperts })}
                </dd>
              </div>
            </dl>
            <p className="text-xs text-gray-500 mt-4">{t('poolSetup.sidebarGrendel')}</p>
            <button
              type="button"
              onClick={opslaan}
              disabled={saving}
              className="mt-5 w-full inline-flex items-center justify-center px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {t('poolSetup.save')}
            </button>
            <button
              type="button"
              onClick={() => navigate('/employer/dashboard/zoeken/kaart')}
              className="mt-2 w-full inline-flex items-center justify-center px-4 py-2.5 border border-blue-200 bg-white text-blue-700 text-sm font-medium rounded-lg hover:bg-blue-50"
            >
              <MapPin className="w-4 h-4 mr-2" />
              {t('poolSetup.mapButton')}
            </button>
            <button
              type="button"
              onClick={() => navigate('/employer/pool-uitnodigingen')}
              className="mt-2 w-full inline-flex items-center justify-center px-4 py-2.5 border border-blue-200 bg-white text-blue-700 text-sm font-medium rounded-lg hover:bg-blue-50"
            >
              <Users className="w-4 h-4 mr-2" />
              {t('poolSetup.inviteButton')}
            </button>
            {payload.config?.afgerond_op && (
              <p className="text-xs text-gray-400 mt-3 text-center">
                {t('poolSetup.lastSaved', {
                  date: new Date(payload.config.afgerond_op).toLocaleDateString(),
                })}
              </p>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
};
